import type { HTMLAttributes, ReactNode } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `solid` = standard surface, `subtle` = inner panel (slate-50 bg). */
  variant?: 'solid' | 'subtle';
  /** Apply the hover-lift interaction (used for course cards / drill cards). */
  interactive?: boolean;
  children: ReactNode;
}

const BASE =
  'rounded-2xl border shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:shadow-none transition-colors';

const VARIANT: Record<NonNullable<CardProps['variant']>, string> = {
  solid: 'bg-white border-slate-200/70 dark:bg-card-dark dark:border-white/10',
  subtle: 'bg-slate-50/60 border-slate-200/70 dark:bg-white/5 dark:border-white/10',
};

const INTERACTIVE =
  'transition-all duration-200 hover:-translate-y-[3px] hover:border-slate-300 hover:shadow-[0_4px_8px_rgba(15,23,42,0.06),0_18px_40px_-12px_rgba(15,23,42,0.20)] dark:hover:border-white/20';

/**
 * Standard surface used throughout the app. All cards share the same
 * rounded-2xl + soft shadow + subtle border. Set `interactive` for
 * clickable cards (drill / domain) that lift on hover.
 */
export function Card({ variant = 'solid', interactive = false, className = '', children, ...rest }: CardProps) {
  return (
    <div className={`${BASE} ${VARIANT[variant]} ${interactive ? INTERACTIVE : ''} ${className}`} {...rest}>
      {children}
    </div>
  );
}
